import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
// 已移除未使用的 pinyin
import ProductGrid from '../BodyComponent/ProductGrid';
import Pagination from '../BodyComponent/Pagination';
import ExplanationProduct from '../BodyComponent/ExplanationProduct';
import CustomModal from '../BodyComponent/CustomModal';
import { getApiUrl } from '../config/api';
import ProductGridSkeleton from '../BodyComponent/ProductGridSkeleton';
import { useQuery } from '@tanstack/react-query';

function ProductCharacterPage() {
    const { character } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // 角色對應字典
    const characterOptionsDict = useMemo(() => ({
        'chiikawa': '小可愛',
        'hachiware': '小八',
        'usagi': '兔兔',
        'momotaro': '小桃',
        'kuri': '栗子',
        'raku': '師傅',
        'sisa': '西薩',
        'furu': '古本屋',
        'anoko': '那孩子',
        'kabutomushi': '小甲蟲',
        'goblin': '哥布林',
        'yoroisan': '鎧甲人',
        'kuma': '白熊',
        'pug': '巴哥',
        'three': '三隻',
        'all': '全員',
        'koroke': '可樂餅',
        'tora': '土龍',
    }), []);

    // 檢查角色是否存在
    useEffect(() => {
        if (!characterOptionsDict[character]) {
            navigate('/stock');
        }
    }, [character, characterOptionsDict, navigate]);

    // 取得目前頁碼
    const page = parseInt(searchParams.get('page')) || 1;

    // 狀態管理
    const [itemsPerPage, setItemsPerPage] = useState(25); // 桌面預設 25，手機 10
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [selectedBarcode, setSelectedBarcode] = useState('');

    // 響應式每頁顯示數量
    const updateItemsPerPage = useCallback(() => {
        const width = window.innerWidth;
        setItemsPerPage(width <= 768 ? 10 : 25);
    }, []);

    useEffect(() => {
        updateItemsPerPage();
        window.addEventListener('resize', updateItemsPerPage);
        // 檢查是否需要重置頁碼
        const shouldReset = sessionStorage.getItem('resetCharacterPage') === 'true';
        if (shouldReset) {
            setSearchParams({ page: '1' }, { replace: true });
            sessionStorage.removeItem('resetCharacterPage');
        }
        return () => window.removeEventListener('resize', updateItemsPerPage);
    }, [updateItemsPerPage, setSearchParams]);

    // 用 useQuery 快取 API 請求
    const { data, isLoading, error } = useQuery({
        queryKey: ['characterProducts', character, page, itemsPerPage],
        queryFn: async () => {
            if (!character || !characterOptionsDict[character]) return { products: [], totalPages: 0 };
            const searchTerm = characterOptionsDict[character];
            const apiUrl = getApiUrl('/orders/get_product_visible_quantity');
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search: searchTerm,
                    page: page,
                    limit: itemsPerPage
                })
            });
            const data = await response.json();
            return {
                products: data.data.map(item => ({
                    id: item.id,
                    barcode: item.條碼,
                    product_name: item.品名,
                    remaining_stock: item.剩餘庫存,
                    visible_quantity: item.現貨系統可販售數,
                    system_price: item.現貨系統價格,
                    system_date: item.現貨系統活動,
                    producer: item.出品商,
                    series: item.系列,
                    product_type: item.類型,
                    role1: item.角色1,
                    role2: item.角色2,
                    role3: item.角色3,
                    specification: item.規格,
                    features: item.其他,
                    others: item.其他,
                    imageUrl: `https://cdn.jsdelivr.net/gh/WaynezProg/product_picture/all/${item.條碼}.webp`,
                    productUrl: `https://chiikawamarket.jp/products/${item.條碼}`
                })),
                totalPages: data.total_pages === 0 ? 1 : data.total_pages || 0
            };
        },
        enabled: !!character && !!characterOptionsDict[character],
        keepPreviousData: true,
        staleTime: 30 * 60 * 1000
    });
    const totalPages = data?.totalPages || 0;

    // page 合法性檢查
    useEffect(() => {
        if (!searchParams.get('page') || isNaN(page) || page < 1 || (totalPages && page > totalPages)) {
            setSearchParams({ page: '1' }, { replace: true });
        }
    }, [searchParams, setSearchParams, page, totalPages]);

    // 滾動到最上方
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page, character]);

    // 分頁切換
    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage.toString() });
    };

    // 使用 CustomModal 來顯示圖片與加入購物車
    const openModal = (imageUrl, barcode) => {
        const img = new Image();
        img.src = imageUrl;
        img.onload = () => {
            setSelectedImage(imageUrl);
            setSelectedBarcode(barcode);
            setIsModalOpen(true);
        };
        img.onerror = () => {
            setSelectedImage('/404.jpg');
            setSelectedBarcode(barcode);
            setIsModalOpen(true);
        };
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImage('');
        setSelectedBarcode('');
    };

    const handleOpenModal = (imageUrl, barcode) => {
        openModal(imageUrl, barcode);
    };

    // 取用快取資料
    const productData = data?.products || [];

    return (
        <div>
            <ExplanationProduct />
            <h1>{characterOptionsDict[character] || '未找到'}</h1>
            {isLoading ? (
                <ProductGridSkeleton count={itemsPerPage} />
            ) : error ? (
                <div>資料載入失敗，請稍後再試。</div>
            ) : (
                <div>
                    <ProductGrid productData={productData} openModal={handleOpenModal} />
                    <Pagination 
                        currentPage={page} 
                        totalPages={totalPages} 
                        onPageChange={handlePageChange} 
                    />
                </div>
            )}
            <CustomModal
                isOpen={isModalOpen}
                onClose={closeModal}
                imageSrc={selectedImage}
                barcode={selectedBarcode}
            />
        </div>
    );
}

export default ProductCharacterPage; 